import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { 
  getCurrentLocation, 
  getLandmarks, 
  getPersonalizedAds,
  markLandmarkVisited,
  dismissLandmark
} from '../services/suggestionsService';

const SuggestionsContext = createContext();

export const useSuggestions = () => {
  const context = useContext(SuggestionsContext);
  if (!context) {
    throw new Error('useSuggestions must be used within a SuggestionsProvider');
  }
  return context;
};

export const SuggestionsProvider = ({ children }) => {
  // Cache configuration
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
  const LOCATION_THRESHOLD = 100; // 100 meters - if user moves more than this, invalidate cache
  
  // State management
  const [location, setLocation] = useState(null);
  const [landmarks, setLandmarks] = useState([]);
  const [ads, setAds] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    hasMore: false,
    total: 0
  });
  
  // Cache state
  const [cacheStatus, setCacheStatus] = useState({
    isCached: false,
    lastUpdated: null,
    cacheKey: null
  });
  
  // Cache storage
  const cacheRef = useRef(new Map());
  const lastLocationRef = useRef(null);
  
  // Helper function to calculate distance between two coordinates
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return R * c; // Distance in meters
  };
  
  // Generate cache key based on location and parameters
  const generateCacheKey = (lat, lng, radius, sortBy) => {
    // Round coordinates to reduce cache fragmentation for nearby locations
    const roundedLat = Math.round(lat * 1000) / 1000; // ~100m precision
    const roundedLng = Math.round(lng * 1000) / 1000;
    return `${roundedLat},${roundedLng},${radius},${sortBy}`;
  };
  
  // Check if cache is valid
  const isCacheValid = useCallback((cacheKey, lastUpdated) => {
    if (!lastUpdated) return false;
    const now = Date.now();
    return (now - lastUpdated) < CACHE_DURATION;
  }, [CACHE_DURATION]);
  
  // Check if location has changed significantly
  const hasLocationChanged = useCallback((newLocation) => {
    if (!lastLocationRef.current) return true;
    
    const distance = calculateDistance(
      lastLocationRef.current.latitude,
      lastLocationRef.current.longitude,
      newLocation.latitude,
      newLocation.longitude
    );
    
    return distance > LOCATION_THRESHOLD;
  }, [LOCATION_THRESHOLD]);
  
  // Get cached data
  const getCachedData = useCallback((cacheKey) => {
    const cached = cacheRef.current.get(cacheKey);
    if (cached && isCacheValid(cacheKey, cached.timestamp)) {
      return cached.data;
    }
    return null;
  }, [isCacheValid]);
  
  // Set cached data
  const setCachedData = (cacheKey, data) => {
    cacheRef.current.set(cacheKey, {
      data,
      timestamp: Date.now()
    });
  };
  
  // Load user location
  const loadUserLocation = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const userLocation = await getCurrentLocation();
      setLocation(userLocation);
      lastLocationRef.current = userLocation;
      
      return userLocation;
    } catch (error) {
      console.error('SuggestionsContext: Error getting location:', error);
      setError('Unable to get your location. Please enable location services and try again.');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Load landmarks with caching
  const loadLandmarks = useCallback(async (params, reset = false) => {
    if (!location) {
      return;
    }
    
    const { radius = 5, sortBy = 'distance', page = 1, limit = 10 } = params;
    const cacheKey = generateCacheKey(location.latitude, location.longitude, radius, sortBy);
    
    // Check if we should use cache
    const shouldUseCache = !reset && 
                          !hasLocationChanged(location) && 
                          isCacheValid(cacheKey, cacheStatus.lastUpdated) &&
                          cacheStatus.cacheKey === cacheKey;
    
    if (shouldUseCache && page === 1) {
      // Use cached data for first page
      const cachedData = getCachedData(cacheKey);
      if (cachedData) {
        setLandmarks(cachedData.landmarks);
        setPagination(cachedData.pagination);
        setCacheStatus({
          isCached: true,
          lastUpdated: cachedData.timestamp,
          cacheKey
        });
        return;
      }
    }
    
    try {
      if (reset || page === 1) {
        setLoading(true);
        if (reset) {
          setLandmarks([]);
          setPagination({ page: 1, hasMore: false, total: 0 });
        }
      } else {
        setLoadingMore(true);
      }
      
      const response = await getLandmarks({
        latitude: location.latitude,
        longitude: location.longitude,
        radius,
        page,
        limit,
        sortBy
      });
      
      if (reset || page === 1) {
        setLandmarks(response.landmarks);
        setPagination({
          page: response.pagination.page,
          hasMore: response.pagination.hasMore,
          total: response.pagination.total
        });
        
        // Cache the first page data
        if (page === 1) {
          const cacheData = {
            landmarks: response.landmarks,
            pagination: {
              page: response.pagination.page,
              hasMore: response.pagination.hasMore,
              total: response.pagination.total
            },
            timestamp: Date.now()
          };
          setCachedData(cacheKey, cacheData);
          setCacheStatus({
            isCached: false,
            lastUpdated: Date.now(),
            cacheKey
          });
        }
      } else {
        setLandmarks(prev => [...prev, ...response.landmarks]);
        setPagination(prev => ({
          ...prev,
          page: response.pagination.page,
          hasMore: response.pagination.hasMore,
          total: response.pagination.total
        }));
      }
      
    } catch (error) {
      console.error('SuggestionsContext: Error loading landmarks:', error);
      setError('Failed to load landmarks. Please try again.');
      throw error;
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [location, cacheStatus, getCachedData, hasLocationChanged, isCacheValid]);
  
  // Load ads with caching
  const loadAds = useCallback(async () => {
    if (!location) return;
    
    const cacheKey = `ads_${generateCacheKey(location.latitude, location.longitude, 0, '')}`;
    
    // Check cache for ads
    const cachedAds = getCachedData(cacheKey);
    if (cachedAds) {
      setAds(cachedAds.ads);
      setUserProfile(cachedAds.userProfile);
      return;
    }
    
    try {
      const response = await getPersonalizedAds(location.latitude, location.longitude);
      setAds(response.ads);
      setUserProfile(response.userProfile);
      
      // Cache ads data
      setCachedData(cacheKey, {
        ads: response.ads,
        userProfile: response.userProfile,
        timestamp: Date.now()
      });
    } catch (error) {
      console.error('Error loading ads:', error);
      // Don't show error for ads as they're not critical
    }
  }, [location, getCachedData]);
  
  // Handle landmark visit
  const handleLandmarkVisit = useCallback(async (landmarkId) => {
    try {
      await markLandmarkVisited(landmarkId);
      setLandmarks(prev => prev.filter(landmark => landmark.id !== landmarkId));
      
      // Update cache if it exists
      if (cacheStatus.cacheKey) {
        const cached = cacheRef.current.get(cacheStatus.cacheKey);
        if (cached) {
          cached.data.landmarks = cached.data.landmarks.filter(landmark => landmark.id !== landmarkId);
          cached.data.pagination.total = Math.max(0, cached.data.pagination.total - 1);
        }
      }
    } catch (error) {
      console.error('Error marking landmark as visited:', error);
      throw error;
    }
  }, [cacheStatus.cacheKey]);
  
  // Handle landmark dismiss
  const handleLandmarkDismiss = useCallback(async (landmarkId) => {
    try {
      await dismissLandmark(landmarkId);
      setLandmarks(prev => prev.filter(landmark => landmark.id !== landmarkId));
      
      // Update cache if it exists
      if (cacheStatus.cacheKey) {
        const cached = cacheRef.current.get(cacheStatus.cacheKey);
        if (cached) {
          cached.data.landmarks = cached.data.landmarks.filter(landmark => landmark.id !== landmarkId);
          cached.data.pagination.total = Math.max(0, cached.data.pagination.total - 1);
        }
      }
    } catch (error) {
      console.error('Error dismissing landmark:', error);
      throw error;
    }
  }, [cacheStatus.cacheKey]);
  
  // Force refresh (bypass cache)
  const forceRefresh = useCallback(async () => {
    if (!location) return;
    
    // Clear cache for current location
    if (cacheStatus.cacheKey) {
      cacheRef.current.delete(cacheStatus.cacheKey);
    }
    
    setCacheStatus({
      isCached: false,
      lastUpdated: null,
      cacheKey: null
    });
    
    // Reload data
    await loadLandmarks({ radius: 5, sortBy: 'distance' }, true);
    await loadAds();
  }, [location, cacheStatus.cacheKey, loadLandmarks, loadAds]);
  
  // Clear all cache
  const clearCache = useCallback(() => {
    cacheRef.current.clear();
    setCacheStatus({
      isCached: false,
      lastUpdated: null,
      cacheKey: null
    });
  }, []);
  
  // Get cache info for UI
  const getCacheInfo = useCallback(() => {
    if (!cacheStatus.isCached || !cacheStatus.lastUpdated) {
      return null;
    }
    
    const age = Date.now() - cacheStatus.lastUpdated;
    const ageMinutes = Math.floor(age / (1000 * 60));
    const remainingMinutes = Math.max(0, Math.floor((CACHE_DURATION - age) / (1000 * 60)));
    
    return {
      ageMinutes,
      remainingMinutes,
      isExpired: age >= CACHE_DURATION
    };
  }, [cacheStatus, CACHE_DURATION]);
  
  const value = {
    // State
    location,
    landmarks,
    ads,
    userProfile,
    loading,
    loadingMore,
    error,
    pagination,
    cacheStatus,
    
    // Actions
    loadUserLocation,
    loadLandmarks,
    loadAds,
    handleLandmarkVisit,
    handleLandmarkDismiss,
    forceRefresh,
    clearCache,
    getCacheInfo,
    
    // Utilities
    hasLocationChanged: (newLocation) => hasLocationChanged(newLocation),
    isCacheValid: (key, timestamp) => isCacheValid(key, timestamp)
  };
  
  return (
    <SuggestionsContext.Provider value={value}>
      {children}
    </SuggestionsContext.Provider>
  );
};
